import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Bot,
  MessageSquare,
  FileText,
  Database,
  Radio,
  ShoppingBag,
  Settings,
  Users,
  BarChart3,
  BookOpen,
  UserCog,
  Play,
  Share2,
  GraduationCap,
  Sparkles,
  Wallet,
  Plus,
  Upload,
  ArrowRight,
  Clock,
  Zap,
  Compass,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/utils/cn';

interface CommandItem {
  id: string;
  label: string;
  icon: LucideIcon;
  action: () => void;
  category: 'recent' | 'actions' | 'navigation';
  keywords?: string[];
}

const RECENT_STORAGE_KEY = 'command-palette-recent';
const MAX_RECENT = 5;

function getRecentIds(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentId(id: string) {
  const recent = getRecentIds().filter((r) => r !== id);
  recent.unshift(id);
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

const CATEGORY_META: Record<string, { label: string; icon: LucideIcon }> = {
  recent: { label: 'Recent', icon: Clock },
  actions: { label: 'Actions', icon: Zap },
  navigation: { label: 'Navigation', icon: Compass },
};

export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const runAndTrack = useCallback(
    (item: CommandItem) => {
      addRecentId(item.id);
      item.action();
      close();
    },
    [close]
  );

  const allItems = useMemo<CommandItem[]>(() => {
    const nav: CommandItem[] = [
      { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, category: 'navigation', action: () => navigate('/'), keywords: ['home', 'overview'] },
      { id: 'nav-agents', label: 'AI Agents', icon: Bot, category: 'navigation', action: () => navigate('/agents'), keywords: ['bot', 'ai'] },
      { id: 'nav-conversations', label: 'Conversations', icon: MessageSquare, category: 'navigation', action: () => navigate('/conversations'), keywords: ['chat', 'messages'] },
      { id: 'nav-documents', label: 'Documents', icon: FileText, category: 'navigation', action: () => navigate('/documents'), keywords: ['library', 'files', 'knowledge'] },
      { id: 'nav-knowledge', label: 'Knowledge Base', icon: Database, category: 'navigation', action: () => navigate('/knowledge'), keywords: ['kb', 'library'] },
      { id: 'nav-knowledge-map', label: 'Knowledge Map', icon: Share2, category: 'navigation', action: () => navigate('/knowledge-map'), keywords: ['graph', 'map'] },
      { id: 'nav-channels', label: 'Channels', icon: Radio, category: 'navigation', action: () => navigate('/channels'), keywords: ['channel', 'deploy'] },
      { id: 'nav-integrations', label: 'Integrations', icon: ShoppingBag, category: 'navigation', action: () => navigate('/integrations'), keywords: ['connect', 'apps'] },
      { id: 'nav-bots', label: 'Sim Bots', icon: UserCog, category: 'navigation', action: () => navigate('/bots'), keywords: ['simulation', 'bot'] },
      { id: 'nav-simulations', label: 'Simulations', icon: Play, category: 'navigation', action: () => navigate('/simulations'), keywords: ['simulate', 'test'] },
      { id: 'nav-training', label: 'Training', icon: GraduationCap, category: 'navigation', action: () => navigate('/training'), keywords: ['train', 'learn'] },
      { id: 'nav-skills', label: 'Skills', icon: Sparkles, category: 'navigation', action: () => navigate('/skills'), keywords: ['ability', 'capability'] },
      { id: 'nav-credits', label: 'Credits', icon: Wallet, category: 'navigation', action: () => navigate('/credits'), keywords: ['balance', 'billing', 'payment'] },
      { id: 'nav-usage', label: 'Usage', icon: BarChart3, category: 'navigation', action: () => navigate('/usage'), keywords: ['analytics', 'stats', 'metrics'] },
      { id: 'nav-docs', label: 'API Docs', icon: BookOpen, category: 'navigation', action: () => navigate('/docs'), keywords: ['api', 'documentation', 'reference'] },
      { id: 'nav-team', label: 'Team', icon: Users, category: 'navigation', action: () => navigate('/team'), keywords: ['members', 'people', 'organization'] },
      { id: 'nav-settings', label: 'Settings', icon: Settings, category: 'navigation', action: () => navigate('/settings'), keywords: ['preferences', 'config'] },
    ];

    const actions: CommandItem[] = [
      { id: 'action-new-agent', label: 'Create new agent', icon: Plus, category: 'actions', action: () => navigate('/agents?action=create'), keywords: ['add', 'new', 'agent', 'bot'] },
      { id: 'action-upload-doc', label: 'Upload document', icon: Upload, category: 'actions', action: () => navigate('/documents?action=upload'), keywords: ['add', 'file', 'import'] },
      { id: 'action-add-channel', label: 'Add channel', icon: Plus, category: 'actions', action: () => navigate('/channels?action=create'), keywords: ['new', 'channel', 'deploy'] },
    ];

    return [...actions, ...nav];
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      const recentIds = getRecentIds();
      const recentItems: CommandItem[] = recentIds
        .map((id) => {
          const item = allItems.find((i) => i.id === id);
          if (!item) return null;
          return { ...item, category: 'recent' as const };
        })
        .filter(Boolean) as CommandItem[];

      const nonRecentIds = new Set(recentItems.map((r) => r.id));
      const otherItems = allItems.filter((i) => !nonRecentIds.has(i.id));

      return [...recentItems, ...otherItems];
    }

    return allItems.filter((item) => {
      const haystack = [item.label, ...(item.keywords || [])].join(' ').toLowerCase();
      return q.split(/\s+/).every((word) => haystack.includes(word));
    });
  }, [query, allItems]);

  // Group items by category for rendering
  const grouped = useMemo(() => {
    const groups: { category: string; items: CommandItem[] }[] = [];
    const order = ['recent', 'actions', 'navigation'];

    for (const cat of order) {
      const items = filtered.filter((i) => i.category === cat);
      if (items.length > 0) {
        groups.push({ category: cat, items });
      }
    }

    return groups;
  }, [filtered]);

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      // Small delay to ensure the DOM is rendered
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  // Reset active index when filtered results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    const activeItem = listRef.current?.querySelector('[data-active="true"]');
    activeItem?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev + 1) % flatItems.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (flatItems[activeIndex]) {
            runAndTrack(flatItems[activeIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    },
    [flatItems, activeIndex, runAndTrack, close]
  );

  if (!open) return null;

  let runningIndex = 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 animate-fade-in"
        onClick={close}
      />

      {/* Palette */}
      <div
        className={cn(
          'relative w-full max-w-lg bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e]',
          'animate-slide-in-from-top',
          'flex flex-col overflow-hidden'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 border-b-2 border-[#1a1a2e]">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search..."
            className="flex-1 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 bg-transparent border-0 outline-none focus:ring-0"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 border-2 border-[#1a1a2e] rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto overscroll-contain py-2">
          {flatItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No results found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            grouped.map((group) => {
              const meta = CATEGORY_META[group.category];
              return (
                <div key={group.category}>
                  <div className="px-4 py-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <meta.icon className="w-3 h-3" />
                    {meta.label}
                  </div>
                  {group.items.map((item) => {
                    const idx = runningIndex++;
                    const isActive = idx === activeIndex;
                    return (
                      <button
                        key={item.id}
                        data-active={isActive}
                        onClick={() => runAndTrack(item)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors',
                          isActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'w-4 h-4 shrink-0',
                            isActive ? 'text-blue-500' : 'text-gray-400'
                          )}
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {isActive && (
                          <ArrowRight className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t-2 border-[#1a1a2e] bg-gray-50 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border-2 border-[#1a1a2e] rounded text-[10px] font-medium">
              &uarr;
            </kbd>
            <kbd className="px-1 py-0.5 bg-white border-2 border-[#1a1a2e] rounded text-[10px] font-medium">
              &darr;
            </kbd>
            <span className="ml-0.5">navigate</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border-2 border-[#1a1a2e] rounded text-[10px] font-medium">
              &crarr;
            </kbd>
            <span className="ml-0.5">select</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-white border-2 border-[#1a1a2e] rounded text-[10px] font-medium">
              esc
            </kbd>
            <span className="ml-0.5">close</span>
          </span>
        </div>
      </div>
    </div>
  );
};
